import React, { useState, useEffect, useRef } from "react";
import { Music, Volume2, VolumeX, Play, Pause, AlignStartVertical } from 'lucide-react';
import "./MusicSelection.css";

const MusicSelection = ({ 
  isVisible, 
  onClose, 
  onAddMusic, 
  onSkipMusic, 
  estimatedDuration 
}) => {
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery] = useState("");
  const [selectedGenre] = useState("all");
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicVolume, setMusicVolume] = useState(0.8);
  const [musicStartTime, setMusicStartTime] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [fadeOut, setFadeOut] = useState(true);
  const audioRef = useRef(null);
  const musicStartPreviewRef = useRef(null);
  
  // Function to load music tracks dynamically from config file
  const loadMusicTracks = async () => {
    try {
      setError(null);
      
      // Fetch the music config file
      const response = await fetch('/music/music-config.json');
      
      if (!response.ok) {
        throw new Error('Failed to load music configuration');
      }
      
      const musicConfig = await response.json();
      
      // Map the config to our track format
      const tracks = musicConfig.map(item => ({
        id: item.id,
        isLocal: true,
        localPath: `/music/${item.filename}`,
        preview_url: `/music/${item.filename}`,
        duration: item.duration,
        user: item.user,
        tags: item.tags,
        title: item.title,
        // Add audio object to get actual duration
        audio: new Audio(`/music/${item.filename}`)
      }));
      
      // Update actual durations
      tracks.forEach(track => {
        if (track.audio) {
          track.audio.addEventListener('loadedmetadata', () => {
            track.duration = track.audio.duration;
          });
          
          track.audio.addEventListener('error', () => {
            console.warn(`Failed to load audio for track: ${track.title}`);
          });
        }
      });
      
      return tracks;
    } catch (error) {
      console.error('Error loading music tracks:', error);
      setError('Failed to load music tracks. Please try again.');
      return getFallbackHardcodedTracks(); // Fallback to hardcoded tracks
    }
  };

  // Fallback function in case dynamic loading fails
  const getFallbackHardcodedTracks = () => {
    return [
      {
        id: 1,
        isLocal: true,
        localPath: "/music/track1.mp3",
        preview_url: "/music/track1.mp3",
        duration: 174,
        user: "Royalty Free",
        tags: "electronic, upbeat, energetic",
        title: "Upbeat Electronic"
      },
      {
        id: 2,
        isLocal: true,
        localPath: "/music/track2.mp3",
        preview_url: "/music/track2.mp3",
        duration: 210,
        user: "Royalty Free",
        tags: "ambient, piano, calm",
        title: "Piano Ambient"
      }
      // Add more fallback tracks if needed
    ];
  };
  
  // Function to fetch music - now using dynamic loading
  const fetchMusic = async (reset = false) => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // If we already have tracks and aren't resetting, filter them
      if (tracks.length > 0 && !reset) {
        const filteredTracks = tracks.filter(track => {
          const matchesSearch = !searchQuery.trim() || 
            track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            track.tags.toLowerCase().includes(searchQuery.toLowerCase());
          
          const matchesGenre = selectedGenre === "all" || 
            track.tags.toLowerCase().includes(selectedGenre.toLowerCase());
          
          return matchesSearch && matchesGenre;
        });
        
        setTracks(filteredTracks);
      } else {
        // Otherwise load tracks dynamically
        const loadedTracks = await loadMusicTracks();
        
        // Filter the loaded tracks
        const filteredTracks = loadedTracks.filter(track => {
          const matchesSearch = !searchQuery.trim() || 
            track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            track.tags.toLowerCase().includes(searchQuery.toLowerCase());
          
          const matchesGenre = selectedGenre === "all" || 
            track.tags.toLowerCase().includes(selectedGenre.toLowerCase());
          
          return matchesSearch && matchesGenre;
        });
        
        setTracks(filteredTracks);
      }
    } catch (err) {
      console.error("Error fetching music:", err);
      setError("Failed to load music. Please try again.");
      // Fallback to local tracks if API fails
      setTracks(getFallbackHardcodedTracks());
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load music when component mounts
  useEffect(() => {
    if (isVisible) {
      fetchMusic(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Load more when search or genre changes
  useEffect(() => {
    if (isVisible) {
      fetchMusic(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedGenre]);
  
  // Handle track selection
  const handleSelectTrack = (track) => {
    setSelectedTrack(track);
    setMusicStartTime(0);
    setVideoStartTime(0);
    
    // Stop currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    setIsPlaying(false);
  };
  
  // Toggle play/pause for the selected track
  const togglePlayPause = () => {
    if (!selectedTrack || !audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.currentTime = musicStartTime;
      audioRef.current.play().catch(error => {
        console.error("Error playing audio:", error);
        setError("Unable to play audio. Your browser may be blocking autoplay.");
      });
    }
    
    setIsPlaying(!isPlaying);
  };
  
  // Update audio when music start time changes
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.currentTime = musicStartTime;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicStartTime]);
  
  // Update audio volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = musicVolume;
    }
  }, [musicVolume]);
  
  // Handle audio errors
  const handleAudioError = (e) => {
    console.error("Audio playback error:", e);
    setError("Unable to play this track. Please try another one.");
    setIsPlaying(false);
  };
  
  // Preview music start time
  const previewMusicStart = () => {
    if (!selectedTrack || !musicStartPreviewRef.current) return;
    
    // Stop main audio if playing
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    
    try {
      musicStartPreviewRef.current.currentTime = musicStartTime;
      musicStartPreviewRef.current.volume = musicVolume;
      
      // Add error handler for preview
      const playPromise = musicStartPreviewRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Error playing preview:", error);
          // Show message to user that browser may be blocking autoplay
          setError("Unable to play preview. Click the play button to listen to the track.");
        });
      }
    } catch (error) {
      console.error("Error in preview playback:", error);
    }
  };
  
  // Format time (seconds to MM:SS)
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  
  // Handle form submission
  const handleAddMusic = () => {
    if (!selectedTrack) return;
    
    // For local tracks
    if (selectedTrack.isLocal) {
      // Create a fetch request to get the file
      fetch(selectedTrack.localPath)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Error loading music file: ${response.statusText}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Create a File object from the blob
          const file = new File([blob], selectedTrack.title.replace(/\s+/g, '_') + ".mp3", { 
            type: blob.type || "audio/mpeg" 
          });
          
          // Add the file to the track and call the callback
          onAddMusic({
            track: {
              ...selectedTrack,
              file: file
            },
            musicStartTime,
            videoStartTime,
            fadeIn,
            fadeOut,
            volume: musicVolume
          });
        })
        .catch(error => {
          console.error("Error preparing music file:", error);
          setError(`Failed to prepare music: ${error.message}`);
        });
    } else {
      // For non-local tracks (fallback)
      onAddMusic({
        track: selectedTrack,
        musicStartTime,
        videoStartTime,
        fadeIn,
        fadeOut,
        volume: musicVolume
      });
    }
  };
  
  if (!isVisible) return null;
  
  return (
    <div className="music-modal-overlay">
      <div className="music-modal">
      <div className="music-modal-header">
        <div className="title-container">
            <span className="music-icon"><Music size={20} /></span>
            <h2>Add Music to<span className="highlight-text"> Highlights</span></h2>
        </div>
        
        </div>
        
        <div className="music-modal-content">
          
          
          <div className="music-list-container">
            {error && <div className="error-message">{error}</div>}
            
            {isLoading ? (
              <div className="no-tracks-message">
                Loading music tracks...
              </div>
            ) : tracks.length === 0 ? (
              <div className="no-tracks-message">
                No music found. Try a different search or category.
              </div>
            ) : (
              <div className="music-list">
                {tracks.map(track => (
                  <div
                    key={track.id}
                    className={`music-item ${selectedTrack?.id === track.id ? "selected" : ""}`}
                    onClick={() => handleSelectTrack(track)}
                  >
                    <div className="music-item-info">
                      <div className="music-title">{track.title || "Untitled"}</div>
                      <div className="music-details">
                        <span className="music-author">By {track.user || "Unknown"}</span>
                      </div>
                    </div>
                    
                    <button 
                      className="music-play-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTrack(track);
                        setTimeout(() => togglePlayPause(), 100);
                      }}
                    >
                      {selectedTrack?.id === track.id && isPlaying ? (
                        <Pause size={18} />
                      ) : (
                        <Play size={18} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {selectedTrack && (
            <div className="music-controls-section">
              <div className="selected-track-info">
                <h3>Selected: {selectedTrack.title || "Untitled"}</h3>
                <div className="music-preview-controls">
                  <button
                    className="play-pause-btn"
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                  
                  <div className="volume-control">
                    <button
                      className="mute-btn"
                      onClick={() => setMusicVolume(musicVolume > 0 ? 0 : 0.8)}
                    >
                      {musicVolume > 0 ? <Volume2 size={18} /> : <VolumeX size={18} />}
                    </button>
                    
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={musicVolume}
                      onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                      className="volume-slider"
                      style={{
                        background: `linear-gradient(to right, #FF4901 0%, #FF4901 ${musicVolume * 100}%, #444 ${musicVolume * 100}%, #444 100%)`
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="timing-controls">
                <div className="timing-control">
                  <label>
                    Start music from:
                    <div className="time-input-group">
                      <input
                        type="range"
                        min="0"
                        max={selectedTrack.duration || 100}
                        value={musicStartTime}
                        onChange={(e) => setMusicStartTime(parseFloat(e.target.value))}
                        className="timing-slider"
                      />
                      <div className="time-display">
                        {formatTime(musicStartTime)}
                        <button 
                          className="preview-btn" 
                          onClick={previewMusicStart}
                          title="Preview start point"
                        >
                          <AlignStartVertical size={14} />
                        </button>
                      </div>
                    </div>
                  </label>
                </div>
                
                <div className="timing-control">
                  <label>
                    Add music at video time:
                    <div className="time-input-group">
                      <input
                        type="range"
                        min="0"
                        max={estimatedDuration || 100}
                        value={videoStartTime}
                        onChange={(e) => setVideoStartTime(parseFloat(e.target.value))}
                        className="timing-slider"
                      />
                      <div className="time-display">
                        {formatTime(videoStartTime)}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="fade-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={fadeIn}
                    onChange={() => setFadeIn(!fadeIn)}
                  />
                  Fade in music
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={fadeOut}
                    onChange={() => setFadeOut(!fadeOut)}
                  />
                  Fade out music
                </label>
              </div>
            </div>
          )}
        </div>
        
        <div className="music-modal-footer">
          <button 
            className="skip-btn"
            onClick={onSkipMusic}
          >
            Skip (No Music)
          </button>
          
          <button
            className="add-music-btn"
            disabled={!selectedTrack}
            onClick={handleAddMusic}
          >
            Add Music to Video
          </button>
        </div>
        
        {/* Hidden audio elements for playback */}
        <audio
          ref={audioRef}
          src={selectedTrack?.preview_url}
          onEnded={() => setIsPlaying(false)}
          onError={handleAudioError}
          hidden
        />
        
        <audio
          ref={musicStartPreviewRef}
          src={selectedTrack?.preview_url}
          onError={handleAudioError}
          hidden
        />
      </div>
    </div>
  );
};

export default MusicSelection;